import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL      || '';
const SUPABASE_ANON_KEY =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  '';

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn('[Supabase] Missing env vars — demo mode');
}

export const supabase = createClient(
  SUPABASE_URL      || 'https://placeholder.supabase.co',
  SUPABASE_ANON_KEY || 'placeholder',
  {
    auth:     { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    realtime: { params: { eventsPerSecond: 15 } },
    global:   { headers: { 'x-app-version': import.meta.env.VITE_APP_VERSION || '1.0.0' } },
  }
);

export const isConfigured = () =>
  !!import.meta.env.VITE_SUPABASE_URL &&
  !import.meta.env.VITE_SUPABASE_URL.includes('placeholder');

// ── Auth ──────────────────────────────────────────────────────────────────────

export const signInAnonymously = async (displayName, avatarColor) => {
  const { data, error } = await supabase.auth.signInAnonymously();
  if (error) throw error;
  if (displayName && data.user) {
    await supabase.auth.updateUser({
      data: { display_name: displayName, avatar_color: avatarColor || randomColor() },
    }).catch((e) => console.warn('[Auth] updateUser failed:', e.message));
  }
  return data;
};

export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}/auth/callback` },
  });
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// ── Session CRUD ──────────────────────────────────────────────────────────────

export const createSession = async ({ name, receipt_url, receipt_items, tax_rate = 0.095, tip_pct = 18 }) => {
  const user = await getCurrentUser();
  if (!user) throw new Error('Must be authenticated to create a session');

  const { data: session, error: sessionErr } = await supabase
    .from('sessions')
    .insert({ name, code: generateCode(), host_id: user.id, status: 'active', receipt_url, tax_rate, tip_pct })
    .select()
    .single();
  if (sessionErr) throw sessionErr;

  if (receipt_items?.length) {
    const { error: itemsErr } = await supabase
      .from('receipt_items')
      .insert(
        receipt_items.map((item, idx) => ({
          session_id: session.id,
          name:       item.name,
          price:      item.price,
          quantity:   item.quantity,
          category:   item.category || 'other',
          emoji:      item.emoji    || '🍽️',
          sort_order: idx,
        }))
      );
    if (itemsErr) throw itemsErr;
  }

  const participant = await joinSession(session.id);
  return { session, participant };
};

export const joinSession = async (sessionId, overrides = {}) => {
  const user = await getCurrentUser();
  if (!user) throw new Error('Must be authenticated to join a session');
  const meta = user.user_metadata || {};

  // Resolve display_name with strongest available source first.
  // Falls back to persisted Settings name to avoid storing 'Guest' on participant rows.
  let storeName = '';
  let storeColor = '';
  try {
    const raw = localStorage.getItem('splitsnap-v2');
    if (raw) {
      const persisted = JSON.parse(raw)?.state || {};
      storeName  = (persisted.defaultName  || '').trim();
      storeColor = persisted.defaultColor || '';
    }
  } catch { /* ignore */ }

  const resolvedName =
    (overrides.displayName || '').trim() ||
    (meta.display_name && meta.display_name !== 'Guest' ? meta.display_name : '') ||
    meta.full_name ||
    storeName ||
    'Guest';

  // Prefer the participant's existing color if they're already in the session;
  // otherwise pick a color unique within the session.
  const { data: existingRow } = await supabase
    .from('participants')
    .select('avatar_color')
    .eq('session_id', sessionId)
    .eq('user_id', user.id)
    .maybeSingle();
  const preferredColor =
    existingRow?.avatar_color ||
    overrides.avatarColor ||
    meta.avatar_color ||
    storeColor ||
    '';
  const resolvedColor = await pickUniqueSessionColor(sessionId, preferredColor);

  // Sync auth metadata so other clients (and future joins) see the real name.
  if (resolvedName !== meta.display_name || resolvedColor !== meta.avatar_color) {
    try {
      await supabase.auth.updateUser({
        data: { display_name: resolvedName, avatar_color: resolvedColor },
      });
    } catch (e) { console.warn('[joinSession] updateUser failed:', e.message); }
  }

  const { data, error } = await supabase
    .from('participants')
    .upsert(
      {
        session_id:   sessionId,
        user_id:      user.id,
        display_name: resolvedName,
        avatar_color: resolvedColor,
        status:       'unpaid',
      },
      { onConflict: 'session_id,user_id' }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const getSessionByCode = async (code) => {
  const clean = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (clean.length !== 6) throw new Error(`Invalid session code: "${code}"`);

  // Uses a SECURITY DEFINER RPC so unjoined users can preview the session by
  // code without triggering RLS (which is restricted to members/host).
  const { data, error } = await supabase.rpc('get_session_preview_by_code', { p_code: clean });
  if (error) throw error;
  if (!data) throw new Error(`Session "${clean}" not found`);
  return normaliseSessionData(data);
};

export const getSessionById = async (sessionId) => {
  const { data, error } = await supabase
    .from('sessions')
    .select('*, receipt_items(*), participants(*)')
    .eq('id', sessionId)
    .single();
  if (error) throw error;
  return normaliseSessionData(data);
};

/** Sort items by sort_order client-side — avoids invalid PostgREST embedded ordering syntax */
const normaliseSessionData = (data) => ({
  ...data,
  receipt_items: [...(data.receipt_items || [])].sort((a, b) => a.sort_order - b.sort_order),
});

// ── Item claiming ─────────────────────────────────────────────────────────────

/**
 * Upsert a claim for `qty` units. Passing qty=0 deletes the claim row.
 * Server-side trigger rejects over-claiming (sum across participants > item.quantity).
 */
export const claimItem = async (itemId, participantId, qty = 1) => {
  if (qty <= 0) return unclaimItem(itemId, participantId);
  const { data, error } = await supabase
    .from('item_claims')
    .upsert(
      { item_id: itemId, participant_id: participantId, claimed_quantity: qty },
      { onConflict: 'item_id,participant_id' }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const unclaimItem = async (itemId, participantId) => {
  const { error } = await supabase
    .from('item_claims')
    .delete()
    .match({ item_id: itemId, participant_id: participantId });
  if (error) throw error;
};

export const getClaimsForSession = async (sessionId) => {
  const { data, error } = await supabase
    .from('item_claims')
    .select('item_id, participant_id, claimed_quantity, receipt_items!inner(session_id)')
    .eq('receipt_items.session_id', sessionId);
  if (error) throw error;
  return data ?? [];
};

// ── Payment tracking ──────────────────────────────────────────────────────────

export const markParticipantPaid = async (participantId, status = 'paid') => {
  const { data, error } = await supabase
    .from('participants')
    .update({ status, paid_at: status === 'paid' ? new Date().toISOString() : null })
    .eq('id', participantId)
    .select()
    .single();
  if (error) throw error;
  // Supabase returns 0 rows if RLS blocks the update — treat as error
  if (!data) throw new Error('Payment update failed: permission denied or participant not found');
  return data;
};

export const recordPayment = async ({ sessionId, toUserId, amount, method, note }) => {
  const user = await getCurrentUser();
  if (!user) throw new Error('Must be authenticated');
  const { data, error } = await supabase
    .from('payments')
    .insert({ session_id: sessionId, from_user_id: user.id, to_user_id: toUserId, amount, method: method || 'other', note, status: 'pending' })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const confirmPayment = async (paymentId) => {
  const { data, error } = await supabase
    .from('payments')
    .update({ status: 'confirmed', confirmed_at: new Date().toISOString() })
    .eq('id', paymentId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// ── Realtime ──────────────────────────────────────────────────────────────────

/**
 * BUG FIX: the previous version called channel.subscribe() internally AND
 * useRealtimeSession called it again → double-subscribe, duplicate events,
 * broken presence tracking.
 *
 * Fix: subscribeToSession now returns the channel WITHOUT subscribing.
 * The caller (useRealtimeSession) is solely responsible for calling .subscribe().
 */
export const buildSessionChannel = (sessionId, callbacks) => {
  const channel = supabase.channel(`session:${sessionId}`, {
    config: { broadcast: { ack: true }, presence: { key: sessionId } },
  });

  channel.on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'participants', filter: `session_id=eq.${sessionId}` },
    (payload) => callbacks.onParticipantChange?.(payload)
  );

  // BUG FIX: no session-level filter available for item_claims in Supabase Realtime.
  // We subscribe to ALL item_claims changes and filter in applyClaimEvent using
  // the known item IDs for this session (passed via the callback).
  channel.on(
    'postgres_changes',
    { event: '*', schema: 'public', table: 'item_claims' },
    (payload) => callbacks.onClaimChange?.(payload)
  );

  channel.on(
    'postgres_changes',
    { event: 'UPDATE', schema: 'public', table: 'sessions', filter: `id=eq.${sessionId}` },
    (payload) => callbacks.onSessionUpdate?.(payload)
  );

  channel
    .on('presence', { event: 'sync'  }, ()               => callbacks.onPresenceSync?.())
    .on('presence', { event: 'join'  }, ({ newPresences }) => callbacks.onPresenceJoin?.({ newPresences }))
    .on('presence', { event: 'leave' }, ({ leftPresences }) => callbacks.onPresenceLeave?.({ leftPresences }));

  return channel; // caller must call .subscribe()
};

/** @deprecated Use buildSessionChannel. Kept for any callers not yet migrated. */
export const subscribeToSession = buildSessionChannel;

export const trackPresence = async (channel, user) => {
  await channel.track({
    user_id:      user.id,
    display_name: user.user_metadata?.display_name || 'Guest',
    online_at:    new Date().toISOString(),
  });
};

// ── Storage ───────────────────────────────────────────────────────────────────

export const uploadReceipt = async (file, sessionId) => {
  const ext  = (file.name || 'receipt.jpg').split('.').pop().toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `receipts/${sessionId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('receipts')
    .upload(path, file, { upsert: true, cacheControl: '3600' });
  if (error) throw error;
  const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(path);
  return publicUrl;
};

// ── Utilities ─────────────────────────────────────────────────────────────────

// Curated, high-contrast participant palette.
// Order = preferred assignment when picking a unique color.
// Purple, Teal, Blue, Green, Orange, Pink, Red, Gold.
export const PARTICIPANT_COLORS = [
  '#7C3AED', // Purple
  '#14B8A6', // Teal
  '#3B82F6', // Blue
  '#22C55E', // Green
  '#F97316', // Orange
  '#EC4899', // Pink
  '#EF4444', // Red
  '#F59E0B', // Gold
];
const COLORS = PARTICIPANT_COLORS;
export const randomColor  = () => COLORS[Math.floor(Math.random() * COLORS.length)];

/** Pick the first palette color not already used in this session.
 *  Falls back to a deterministic palette slot if all are taken. */
export const pickUniqueSessionColor = async (sessionId, preferredColor) => {
  try {
    const { data } = await supabase
      .from('participants')
      .select('avatar_color')
      .eq('session_id', sessionId);
    const used = new Set((data || []).map((p) => (p.avatar_color || '').toLowerCase()));
    if (preferredColor && !used.has(preferredColor.toLowerCase())) return preferredColor;
    const free = PARTICIPANT_COLORS.find((c) => !used.has(c.toLowerCase()));
    if (free) return free;
    // All taken — cycle deterministically by count
    return PARTICIPANT_COLORS[(data?.length || 0) % PARTICIPANT_COLORS.length];
  } catch {
    return preferredColor || randomColor();
  }
};
export const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};
